import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DiaryInterface } from 'src/app/models/interface/diary.interface';
import { InterventionInterface } from 'src/app/models/interface/intervention.interface';
import { LessonInterface } from 'src/app/models/interface/lesson.interface';
import { StudyInterface } from 'src/app/models/interface/study/study.interface';
import { DiaryStore } from '../../../store/diary/component-store/diary.store';
import { InterventionStore } from '../../../store/intervention/component-store/intervention.store';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { HelperService } from 'src/app/services/helper/helper.service';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { QuestionnaireStore } from 'src/app/store/lesson-questionnaire/component-store/lesson-questionnaire.store';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';

type QuestionnaireQuestion =
  YesNoQuestion | ChoiceQuestions | SliderQuestion | DateTimeQuestions | TextQuestions | QuestionTableQuestion;
type QuestionnaireQuestionTranslations = QuestionnaireQuestion['translations'];

type ResponseStatus = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE';

@Component({
  selector: 'app-codebook-overview',
  templateUrl: './codebook-overview.component.html',
  styleUrls: ['./codebook-overview.component.scss'],
  providers: [InterventionStore, DiaryStore, StudyStore, QuestionnaireStore],
})
export class CodebookOverviewComponent implements OnDestroy, OnInit {
  public displayedColumns = ['position', 'questionType', 'questionLabel', 'question', 'answer', 'raw'] as const;
  public dataSource: MatTableDataSource<QuestionnaireQuestion>;

  // Filter
  public filter = {
    interventionSelection: '',
    diarySelection: '',
    questionnaireSelection: '',
  };

  public study: StudyInterface;

  public interventions: InterventionInterface[] = [];
  public interventionsSubject = new BehaviorSubject<InterventionInterface[]>([]);
  public interventions$ = this.interventionsSubject.asObservable();
  public selectedIntervention: InterventionInterface;

  public diaries: DiaryInterface[] = [];
  public diariesSubject = new BehaviorSubject<DiaryInterface[]>([]);
  public diaries$ = this.diariesSubject.asObservable();
  public selectedDiary: DiaryInterface;

  public questionnaires: LessonInterface[] = [];
  public questionnairesSubject = new BehaviorSubject<LessonInterface[]>([]);
  public questionnaires$ = this.questionnairesSubject.asObservable();
  public selectedQuestionnaire: LessonInterface;

  public questionnaireElements: QuestionnaireQuestion[] = [];

  public diariesOfStudy$: Observable<DiaryInterface[]>;

  public questionnairesOfIntervention$: Observable<LessonInterface[]>;
  public questionnaireOfDiary$: Observable<LessonInterface>;

  public selectedLanguage: string;
  public languages: string[] = [];

  public exportInterventionCodebookResponse = new BehaviorSubject<ResponseStatus>('DEFAULT');
  public exportDiaryCodebookResponse = new BehaviorSubject<ResponseStatus>('DEFAULT');

  private exportInterventionCodebookResponse$: Observable<any>;
  private exportDiaryCodebookResponse$: Observable<any>;

  private elementsOfQuestionnaire$: Observable<any[]>;
  private allInterventionsOfStudy$: Observable<InterventionInterface[]>;

  private subscriptions: Subscription[] = [];

  constructor(
    private questionnaireStore: QuestionnaireStore,
    private interventionStore: InterventionStore,
    private diaryStore: DiaryStore,
    private helperService: HelperService,
    private translateService: TranslateService,
  ) {
    this.allInterventionsOfStudy$ = this.interventionStore.allInterventionsOfSpecificStudy$;
    this.diariesOfStudy$ = this.diaryStore.diariesOfStudy$;
    this.questionnairesOfIntervention$ = this.questionnaireStore.questionnairesOfIntervention$;
    this.questionnaireOfDiary$ = this.diaryStore.questionnaireOfDiary$;
    this.elementsOfQuestionnaire$ = this.questionnaireStore.elementsOfQuestionnaire$;

    this.exportInterventionCodebookResponse$ = this.interventionStore.exportInterventionCodebookResponse$;
    this.exportDiaryCodebookResponse$ = this.diaryStore.exportDiaryCodebookResponse$;
  }

  public get helper() {
    return this.helperService;
  }

  @Input()
  set _study(_study: StudyInterface) {
    if (_study) {
      this.study = _study;
    }
  }

  ngOnInit(): void {
    this.interventionStore.getAllInterventionsOfSpecificStudy({
      studyId: this.study.id,
      interventionType: undefined,
      isActive: 1,
    });

    this.subscriptions.push(
      this.allInterventionsOfStudy$.subscribe((interventions) => {
        this.interventions = interventions;
        this.interventionsSubject.next(this.interventions);
        this.selectedIntervention = this.interventions.at(0);
        if (this.selectedIntervention) {
          this.getQuestionnairesOfIntervention(this.selectedIntervention.id);
        }
      }),
    );

    this.subscriptions.push(
      this.questionnairesOfIntervention$.subscribe((result) => {
        this.questionnaires = result;
        this.questionnairesSubject.next(this.questionnaires);
        if (result.length > 0) {
          this.selectedQuestionnaire = result[0];
          this.languages = this.getLanguages(this.selectedIntervention.attributes.locales);
          this.selectedLanguage = this.getSelectedLanguage(this.languages, this.selectedLanguage);
          this.changeSelection();
        }
      }),
    );

    this.diaryStore.getDiariesOfStudy({ studyId: this.study.id });
    this.subscriptions.push(
      this.diariesOfStudy$.subscribe((diaries) => {
        const studyId = this.study.id.toString();
        this.diaries = diaries.filter(
          (diary) => diary.attributes.study_id.toString() === studyId,
        );
        this.diariesSubject.next(this.diaries);
      }),
    );

    this.subscriptions.push(
      this.questionnaireOfDiary$.subscribe((result) => {
        this.questionnaires = [result];
        this.questionnairesSubject.next(this.questionnaires);
        if (result !== null) {
          this.selectedQuestionnaire = result;
          this.languages = this.getLanguages(this.selectedDiary.attributes.locales);
          this.selectedLanguage = this.getSelectedLanguage(this.languages, this.selectedLanguage);
          this.changeSelection();
        }
      }),
    );

    this.subscriptions.push(
      this.elementsOfQuestionnaire$.subscribe((result) => {
        this.questionnaireElements = result.filter((element) => !!element?.label);
        this.dataSource = new MatTableDataSource(this.questionnaireElements);
      }),
    );

    this.subscriptions.push(
      this.exportInterventionCodebookResponse$.subscribe((value) => {
        if (!value) { return; }
        if (value instanceof HttpErrorResponse) {
          this.exportInterventionCodebookResponse.next('FAILURE');
          setTimeout(() => {
            this.exportInterventionCodebookResponse.next('DEFAULT');
          }, 2500);
        } else {
          if (value.state && value.state === 'DONE') {
            this.exportInterventionCodebookResponse.next('SUCCESS');
            setTimeout(() => {
              this.exportInterventionCodebookResponse.next('DEFAULT');
            }, 2500);
          }
        }
      }),
    );

    this.subscriptions.push(
      this.exportDiaryCodebookResponse$.subscribe((value) => {
        if (!value) { return; }
        if (value instanceof HttpErrorResponse) {
          this.exportDiaryCodebookResponse.next('FAILURE');
          setTimeout(() => {
            this.exportDiaryCodebookResponse.next('DEFAULT');
          }, 2500);
        } else {
          if (value.state && value.state === 'DONE') {
            this.exportDiaryCodebookResponse.next('SUCCESS');
            setTimeout(() => {
              this.exportDiaryCodebookResponse.next('DEFAULT');
            }, 2500);
          }
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private getLanguages(locales: string[]): string[] {
    if (this.isNonEmptyArray(locales)) {
      return locales;
    } else {
      const questionnaireLocales = this.selectedQuestionnaire.attributes.locales;
      if (this.isNonEmptyArray(questionnaireLocales)) {
        return questionnaireLocales;
      }
    }
    return [];
  }

  private getSelectedLanguage(languages: string[], language: string): string {
    if (!language || !languages.includes(language)) {
      return languages.length > 0 ? languages[0] : null;
    }
    return language;
  }

  public getQuestionnairesOfIntervention(interventionId: number) {
    this.selectedDiary = undefined;
    // Triggers subscription of questionnairesOfIntervention$
    this.questionnaireStore.getQuestionnairesIntervention({ interventionId });
  }

  public getQuestionnaireOfDiary(diaryId: number) {
    this.selectedIntervention = undefined;
    // Triggers subscription of questionnaireOfDiary$
    this.diaryStore.getQuestionnaireOfDiary({ diaryId });
  }

  public getElementsOfQuestionnaire(questionnaireId: number) {
    // Triggers subscription of elementsOfQuestionnaire$
    this.questionnaireStore.getElementsOfQuestionnaire({ questionnaireId });
  }

  public handleInterventionFilter() {
    const input = this.filter.interventionSelection.toLowerCase();
    const filteredInterventions = this.interventions.filter(
      (intervention) => this.getDropdownName(intervention).toLowerCase().includes(input),
    );
    if (filteredInterventions.length > 0) {
      this.interventionsSubject.next(filteredInterventions);
    } else {
      this.interventionsSubject.next([this.selectedIntervention]);
    }
  }

  public handleDiaryFilter() {
    const input = this.filter.diarySelection.toLowerCase();
    const filteredDiaries = this.diaries.filter(
      (diary) => this.getDropdownName(diary).toLowerCase().includes(input),
    );
    if (filteredDiaries.length > 0) {
      this.diariesSubject.next(filteredDiaries);
    } else {
      this.diariesSubject.next([this.selectedDiary]);
    }
  }

  public handleQuestionnaireFilter() {
    const input = this.filter.questionnaireSelection.toLowerCase();
    const filteredQuestionnaires = this.questionnaires.filter(
      (qre) => this.getDropdownName(qre).toLowerCase().includes(input),
    );
    if (filteredQuestionnaires.length > 0) {
      this.questionnairesSubject.next(filteredQuestionnaires);
    } else {
      this.questionnairesSubject.next([this.selectedQuestionnaire]);
    }
  }

  public idEquals<T extends { id: number }>(obj1: T, obj2: T): boolean {
    return obj1.id === obj2.id;
  }

  public getDropdownName(obj: { id: number, attributes: { name: string } } | null): string {
    return `${obj?.attributes.name} (ID ${obj?.id})`;
  }

  public resetInterventionFilter(): void {
    this.filter.interventionSelection = '';
    this.interventionsSubject.next(this.interventions);
  }

  public resetDiaryFilter(): void {
    this.filter.diarySelection = '';
    this.diariesSubject.next(this.diaries);
  }

  public resetQuestionnaireFilter(): void {
    this.filter.questionnaireSelection = '';
    this.questionnairesSubject.next(this.questionnaires);
  }

  public changeSelection() {
    this.getElementsOfQuestionnaire(this.selectedQuestionnaire.id);
  }

  private getSelectedTranslation<T extends QuestionnaireQuestionTranslations>(
    translations: T,
  ): T[number] | undefined {
    if (this.isEmptyArray((translations))) {
      return undefined;
    }
    let translation: T[number] = translations[0];
    if (translation.locale === this.selectedLanguage) {
      return translation;
    }
    for (let i = 1; i < translations.length; i++) {
      const element = translations[i];
      const locale = element.locale;
      if (locale === this.selectedLanguage) {
        translation = element;
        break;
      } else if (locale === "en") {
        translation = element;
      }
    }
    return translation;
  }

  public getCodebookQuestion<T extends QuestionnaireQuestionTranslations>(
    translations: T,
  ): T[number]['question'] | '' {
    const translation = this.getSelectedTranslation(translations);
    return translation !== undefined ? translation.question : '';
  }

  private getYesNoAnswers(language: string) {
    switch (language) {
      case 'ca': return 'Sí | No';
      case 'de': return 'Ja | Nein';
      case 'en': return 'Yes | No';
      case 'es': return 'Sí | No';
      case 'fr': return 'Oui | Non';
      case 'ro': return 'Da | Nu';
      default: return 'Yes | No';
    }
  }

  public getCodebookAnswer(element: QuestionnaireQuestion): Observable<string | string[]> {
    switch (element.questiontype) {
      case 'YesNoSwitch': {
        return of(this.getYesNoAnswers(this.selectedLanguage));
      }
      case 'SingleChoice':
      case 'MultipleChoice': {
        const translation = this.getSelectedTranslation(element.translations);
        const answers = translation !== undefined ? translation.answers : '';
        return of(answers);
      }
      case 'Slider': {
        const translation = this.getSelectedTranslation(element.translations);
        if (translation !== undefined) {
          const answer = translation.answers;
          const { label: minLabel, value: minValue } = answer[0];
          const { label: maxLabel, value: maxValue } = answer[1];
          return of(`${minLabel}(${minValue}) - ${maxLabel}(${maxValue})`);
        } else {
          return of('');
        }
      }
      case 'TextDate': {
        const value = element.values[0];
        if (value === 'date') {
          return this.translateService.get('codebook.date');
        } else if (value === 'datetime') {
          return this.translateService.get('codebook.datetime');
        } else if (value === 'time') {
          return this.translateService.get('codebook.time');
        } else {
          return of('');
        }
      }
      case 'TextString':
      case 'TextArea': {
        return this.translateService.get('codebook.textinput');
      }
      case 'QuestionTable': {
        const translation = this.getSelectedTranslation(element.translations);
        const answers = translation !== undefined ? translation.answers : '';
        return of(answers);
      }
      default:
        return of('');
    }
  }

  public getCodebookRawAnswer(element: QuestionnaireQuestion) {
    switch (element.questiontype) {
      case 'YesNoSwitch':
      case 'SingleChoice':
      case 'MultipleChoice': {
        return element.values;
      }
      case 'TextDate': {
        const value = element.values[0];
        if (value === 'date') {
          return '(DD-MM-YYYY)';
        } else if (value === 'datetime') {
          return '(DD-MM-YYYY HH:MM)';
        } else if (value === 'time') {
          return '(HH:MM)';
        } else {
          return '';
        }
      }
      case 'Slider':
      case 'QuestionTable': {
        return this.helper.stringifyJSON(element.values);
      }
      case 'TextString':
      case 'TextArea':
      default:
        return '';
    }
  }

  public exportInterventionCodebook(interventionId: number): void {
    if (this.exportInterventionCodebookResponse.value === 'DEFAULT') {
      this.exportInterventionCodebookResponse.next('LOADING');
      // Triggers subscription of exportInterventionCodebookResponse$
      this.interventionStore.exportCSVInterventionCodebook({
        interventionId: interventionId,
        language: this.selectedLanguage,
      });
    }
  }

  public exportDiaryCodebook(diaryId: number): void {
    if (this.exportDiaryCodebookResponse.value === 'DEFAULT') {
      this.exportDiaryCodebookResponse.next('LOADING');
      // Triggers subscription of exportDiaryCodebookResponse$
      this.diaryStore.exportCSVDiaryCodebook({
        diaryId: diaryId,
        language: this.selectedLanguage,
      });
    }
  }

  private isNonEmptyArray(arr: any): arr is any[] {
    return Array.isArray(arr) && arr.length > 0;
  }

  private isEmptyArray(arr: any): boolean {
    return !this.isNonEmptyArray(arr);
  }
}

type QuestionnaireQuestionBase = {
  id: number;
  elementtype: 'elements/questions';
  name: 'QUESTION';
  label: string;
  position: number;
  required: 0 | 1;
  is_active: 0 | 1;
};

type YesNoQuestion = QuestionnaireQuestionBase & {
  questiontype: 'YesNoSwitch';
  values: [true, false] | [false, true];
  translations: {
    locale: string;
    question: string;
    answers: [];
  }[];
};

type ChoiceQuestions = QuestionnaireQuestionBase & {
  questiontype: 'SingleChoice' | 'MultipleChoice';
  values: string[];
  translations: {
    locale: string;
    question: string;
    answers: string[];
  }[];
};

type SliderQuestion = {
  questiontype: 'Slider';
  values: {
    min: number;
    max: number;
    step: number;
    start: number;
  };
  translations: {
    locale: string;
    question: string;
    answers: SliderAnswerTranslation;
  }[];
};

type SliderAnswerTranslation = [
  { value: number; label: string }, // Minimum values
  { value: number; label: string }, // Maximum values
];

type DateTimeQuestions = {
  questiontype: 'TextDate';
  values: ['date'] | ['time'] | ['datetime'];
  translations: {
    locale: string;
    question: string;
    answers: [];
  }[];
};

type TextQuestions = QuestionnaireQuestionBase & {
  questiontype: 'TextString' | 'TextArea';
  values?: null;
  translations: {
    locale: string;
    question: string;
    answers: [];
  }[];
};

type QuestionTableQuestion = QuestionnaireQuestionBase & {
  questiontype: 'QuestionTable';
  values: {
    min: number;
    max: number;
    questions: number;
  };
  translations: {
    locale: string;
    question: string[];
    answers: string[];
  }[];
};
